/**
 * Typed wrappers for the per-conference attendee-sheet endpoints (super-admin
 * plane only — the capability is console-only, so these types live here
 * rather than in @viq-admin/types).
 *
 * Two-step flow: `analyzeSheet` parses the workbook and proposes a column
 * mapping without writing anything; `ingestSheet` persists it, REPLACING the
 * conference's active sheet. Neither call sends `?companyId=` — the conference
 * id in the path is the scope.
 */
import { superadminFetch } from './dataProvider';

export type CanonicalField =
  | 'first_name'
  | 'last_name'
  | 'company'
  | 'title'
  | 'phone'
  | 'email'
  | 'address'
  | 'city'
  | 'state'
  | 'postal_code'
  | 'country';

/** Header label -> canonical field (null = column not imported). */
export type SheetMapping = Record<string, CanonicalField | null>;

export interface AnalyzeSheetRequest {
  fileName: string;
  /** The .xlsx bytes, base64-encoded (max 20MB decoded). */
  contentBase64: string;
}

export interface AnalyzeSheetResponse {
  worksheetName: string;
  header: string[];
  headerRowNumber: number;
  mapping: SheetMapping;
  unmapped: string[];
  /** Subset of `unmapped`: obvious alias match blocked by an earlier column. */
  conflicted: string[];
  mappingSource: 'model' | 'alias';
  rowCount: number;
  /** First 5 data rows, columns aligned to `header`. */
  preview: string[][];
  contentHash: string;
}

export interface IngestSheetRequest extends AnalyzeSheetRequest {
  mapping: SheetMapping;
  /** contentHash from analyze for the same file (required by the server). */
  expectedContentHash: string;
}

export interface RejectedRow {
  /** Spreadsheet line number. */
  line: number;
  reason: string;
}

export interface IngestSheetResponse {
  sheetId: string;
  /** Accepted rows only. */
  rowCount: number;
  rejected: RejectedRow[];
  worksheetName: string;
}

/** POST /v1/superadmin/conferences/{id}/attendee-sheet/analyze — writes nothing. */
export async function analyzeSheet(
  conferenceId: string,
  body: AnalyzeSheetRequest,
): Promise<AnalyzeSheetResponse> {
  return superadminFetch<AnalyzeSheetResponse>(
    `/v1/superadmin/conferences/${conferenceId}/attendee-sheet/analyze`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/**
 * POST /v1/superadmin/conferences/{id}/attendee-sheet — replaces the active
 * sheet and resets lead match history. 409 = file differs from the analyzed
 * one; 422 = mapping lacks first/last name or the workbook can't be parsed.
 */
export async function ingestSheet(
  conferenceId: string,
  body: IngestSheetRequest,
): Promise<IngestSheetResponse> {
  return superadminFetch<IngestSheetResponse>(
    `/v1/superadmin/conferences/${conferenceId}/attendee-sheet`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}
